import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";
import DashboardSidebar from "@/pages/dashboard/components/DashboardSidebar";
import AgencyStats from "./components/AgencyStats";
import SubMemberTable from "./components/SubMemberTable";
import AgencyChart from "./components/AgencyChart";
import RecentSends from "./components/RecentSends";

function generateBasePath(): string {
  const base = __BASE_PATH__;
  const parts = base.split('/').filter(Boolean);
  return parts.length > 0 ? '/' + parts.join('/') : '';
}

const pathPrefix = generateBasePath();

interface Member {
  id: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  status: string;
  credit: number;
  plan: string;
  total_sent: number;
  total_charged: number;
  per_message_rate: number | null;
  created_at: string;
  updated_at: string;
}

interface MonthlyStat {
  month: string;
  sent: number;
  success: number;
  fail: number;
}

interface RecentSend {
  id: string;
  type: string;
  status: string;
  total_count: number;
  success_count: number;
  fail_count: number;
  sent_at: string;
}

interface StatsData {
  referral_code: string | null;
  totalMembers: number;
  totalSent: number;
  totalSuccess: number;
  totalFail: number;
  activeMembers: number;
  monthlyStats: MonthlyStat[];
  recentSends: RecentSend[];
}

export default function AgencyPage() {
  const navigate = useNavigate();
  const { user, loading, session, isAgency, roleLoading } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [stats, setStats] = useState<StatsData | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
  const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

  const inviteUrl = stats?.referral_code
    ? `${window.location.origin}${pathPrefix}/login?tab=signup&ref=${stats.referral_code}`
    : "";

  const qrCodeUrl = inviteUrl
    ? `https://chart.googleapis.com/chart?cht=qr&chs=160x160&chl=${encodeURIComponent(inviteUrl)}`
    : "";

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!loading && !roleLoading && user && !isAgency) {
      navigate("/dashboard");
    }
  }, [user, isAgency, loading, roleLoading, navigate]);

  const fetchData = useCallback(async () => {
    if (!user || !session) return;
    setLoadingData(true);

    let accessToken: string | null = null;
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (!currentSession) {
        setLoadingData(false);
        return;
      }
      accessToken = currentSession.access_token;
    } catch (err: any) {
      console.error("[Agency] getSession error:", err);
      setLoadingData(false);
      return;
    }

    try {
      const [statsRes, membersRes] = await Promise.all([
        fetch(`${SUPABASE_URL}/functions/v1/get-agency-stats`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: SUPABASE_ANON_KEY,
            Authorization: `Bearer ${accessToken}`,
          },
        }),
        fetch(`${SUPABASE_URL}/functions/v1/get-agency-members`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: SUPABASE_ANON_KEY,
            Authorization: `Bearer ${accessToken}`,
          },
        }),
      ]);

      const statsData = await statsRes.json();
      const membersData = await membersRes.json();

      if (statsData.error) {
        console.error("[Agency] stats error:", statsData.error);
      } else {
        setStats(statsData);
      }

      if (membersData.error) {
        console.error("[Agency] members error:", membersData.error);
      } else {
        setMembers(membersData.members ?? []);
      }
    } catch (err) {
      console.error("[Agency] fetch error:", err);
    } finally {
      setLoadingData(false);
    }
  }, [user, session, SUPABASE_URL, SUPABASE_ANON_KEY]);

  useEffect(() => {
    if (user && session && isAgency) {
      fetchData();
    }
  }, [user, session, isAgency, fetchData]);

  const handleCopyCode = useCallback(() => {
    if (!stats?.referral_code) return;
    navigator.clipboard.writeText(stats.referral_code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [stats?.referral_code]);

  const handleCopyLink = useCallback(() => {
    if (!inviteUrl) return;
    navigator.clipboard.writeText(inviteUrl).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  }, [inviteUrl]);

  const handleShareKakao = useCallback(() => {
    if (!inviteUrl) return;
    const text = `Signal SMS 에이전시에 가입하세요!\n${inviteUrl}`;
    window.open(`https://sharer.kakao.com/talk/friends/picker/link?app_key=&url=${encodeURIComponent(inviteUrl)}`, "_blank");
  }, [inviteUrl]);

  const handleShareEmail = useCallback(() => {
    if (!inviteUrl) return;
    const subject = encodeURIComponent("Signal SMS 에이전시 가입 초대");
    const body = encodeURIComponent(`안녕하세요,\n\nSignal SMS 에이전시에 가입하세요!\n아래 링크를 클릭하면 추천인 코드가 자동으로 적용됩니다.\n\n${inviteUrl}\n\n감사합니다.`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  }, [inviteUrl]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a18] flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-amber-400 text-3xl"></i>
      </div>
    );
  }

  if (!user) return null;

  const sidebarWidth = collapsed ? "ml-16" : "ml-60";

  return (
    <div className="min-h-screen bg-[#080812] flex">
      <DashboardSidebar collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />

      <main className={`flex-1 ${sidebarWidth} transition-all duration-300 min-h-screen`}>
        {/* Top bar */}
        <header className="h-16 bg-[#0a0a18]/80 backdrop-blur-md border-b border-white/[0.06] flex items-center justify-between px-6 sticky top-0 z-30">
          <div>
            <h1 className="text-white font-semibold text-base">에이전시 대시보드</h1>
            <p className="text-gray-500 text-xs">하위 회원 및 발송 현황 관리</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-amber-400 text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 whitespace-nowrap">
              <i className="ri-building-4-line mr-1"></i>
              에이전시
            </span>
          </div>
        </header>

        {/* Page content */}
        <div className="p-6 space-y-6">
          {/* Referral Code & Invite Link Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Referral Code Banner */}
            {stats?.referral_code && (
              <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-2xl p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-500/10 border border-amber-500/20 shrink-0">
                    <i className="ri-gift-line text-amber-400 text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-sm">나의 추천인 코드</h3>
                    <p className="text-gray-500 text-xs mt-0.5">
                      하위 회원이 가입 시 이 코드를 입력하면 자동으로 소속됩니다
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCopyCode}
                  className="flex items-center gap-2 px-5 py-2.5 bg-amber-600 hover:bg-amber-500 text-white text-sm font-semibold rounded-xl cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className={`${copied ? "ri-checkbox-circle-line" : "ri-file-copy-line"} text-sm`}></i>
                  {copied ? "복사 완료" : stats.referral_code}
                </button>
              </div>
            )}
            {!stats?.referral_code && !loadingData && (
              <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-5 flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-500/10 border border-gray-500/20 shrink-0">
                  <i className="ri-gift-line text-gray-500 text-lg"></i>
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm">추천인 코드 미설정</h3>
                  <p className="text-gray-500 text-xs mt-0.5">
                    관리자에게 추천인 코드 생성을 요청해 주세요. 코드가 있어야 하위 회원을 자동으로 받을 수 있습니다.
                  </p>
                </div>
              </div>
            )}

            {/* Invite Link Banner */}
            {stats?.referral_code && (
              <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 rounded-2xl p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-emerald-500/10 border border-emerald-500/20 shrink-0">
                    <i className="ri-link text-emerald-400 text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-sm">하위 회원 초대 링크</h3>
                    <p className="text-gray-500 text-xs mt-0.5">
                      링크를 공유하면 자동으로 추천인 코드가 적용된 회원가입 페이지로 연결
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCopyLink}
                  className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold rounded-xl cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className={`${linkCopied ? "ri-checkbox-circle-line" : "ri-file-copy-line"} text-sm`}></i>
                  {linkCopied ? "복사 완료" : "링크 복사"}
                </button>
              </div>
            )}
          </div>

          {/* Invite Link Detail Card */}
          {stats?.referral_code && (
            <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-share-line text-emerald-400 text-sm"></i>
                <h3 className="text-white font-semibold text-sm">초대 링크 공유</h3>
              </div>
              <div className="flex flex-col lg:flex-row gap-5">
                {/* URL + Copy */}
                <div className="flex-1 flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                        <i className="ri-link text-gray-500 text-sm"></i>
                      </div>
                      <input
                        type="text"
                        readOnly
                        value={inviteUrl}
                        className="w-full bg-white/[0.05] border border-white/[0.1] rounded-xl pl-9 pr-4 py-3 text-white text-sm focus:outline-none"
                      />
                    </div>
                    <button
                      onClick={handleCopyLink}
                      className="shrink-0 flex items-center gap-2 px-4 py-3 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold rounded-xl cursor-pointer whitespace-nowrap transition-colors"
                    >
                      <i className={`${linkCopied ? "ri-checkbox-circle-line" : "ri-file-copy-line"} text-sm`}></i>
                      {linkCopied ? "복사 완료" : "복사"}
                    </button>
                  </div>

                  {/* Share buttons */}
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={handleShareKakao}
                      className="flex items-center gap-2 px-4 py-2 bg-[#FEE500] hover:bg-[#FDD835] text-black text-sm font-medium rounded-lg cursor-pointer whitespace-nowrap transition-colors"
                    >
                      <i className="ri-kakao-talk-fill text-base"></i>
                      카카오톡 공유
                    </button>
                    <button
                      onClick={handleShareEmail}
                      className="flex items-center gap-2 px-4 py-2 bg-white/[0.08] hover:bg-white/[0.12] border border-white/[0.12] text-white text-sm font-medium rounded-lg cursor-pointer whitespace-nowrap transition-colors"
                    >
                      <i className="ri-mail-send-line text-base"></i>
                      이메일 공유
                    </button>
                  </div>

                  <p className="text-gray-500 text-xs">
                    <i className="ri-information-line mr-1"></i>
                    링크를 클릭한 사용자는 회원가입 페이지로 이동하며, 추천인 코드가 자동으로 입력됩니다.
                  </p>
                </div>

                {/* QR Code */}
                <div className="shrink-0 flex flex-col items-center gap-2">
                  <div className="bg-white rounded-xl p-2.5">
                    <img
                      src={qrCodeUrl}
                      alt="QR Code"
                      className="w-32 h-32 object-contain"
                      width="128"
                      height="128"
                    />
                  </div>
                  <p className="text-gray-500 text-xs">QR 코드 스캔</p>
                </div>
              </div>
            </div>
          )}

          <AgencyStats
            totalMembers={stats?.totalMembers ?? 0}
            totalSent={stats?.totalSent ?? 0}
            totalSuccess={stats?.totalSuccess ?? 0}
            totalFail={stats?.totalFail ?? 0}
            activeMembers={stats?.activeMembers ?? 0}
            loading={loadingData}
          />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <SubMemberTable members={members} loading={loadingData} onRefresh={fetchData} />
            </div>
            <div>
              <RecentSends sends={stats?.recentSends ?? []} loading={loadingData} />
            </div>
          </div>

          <AgencyChart monthlyStats={stats?.monthlyStats ?? []} loading={loadingData} />
        </div>
      </main>
    </div>
  );
}